import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useOrganization } from '../../context/OrganizationContext'
import { useToast } from '../../context/ToastContext'
import type { ApiClientError } from '../../services/apiHelpers'
import { normalizeStorefront } from '../../utils/storefront'
import { OrganizationForm } from './OrganizationForm'
import { SkeletonFormSections } from '../../components/ui/Skeleton'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import {
  statusChangeConfirmMessage,
  statusChangeNeedsConfirmation,
} from '../../utils/orgStatus'

export default function OrganizationEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { getOrganizationById, updateOrganization, clearError, error } = useOrganization()
  const { pushToast } = useToast()
  const [initial, setInitial] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})
  const [pendingPayload, setPendingPayload] = useState(null)
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false)

  useEffect(() => {
    clearError()
  }, [clearError, id])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const org = await getOrganizationById(id)
        if (!cancelled) {
          setInitial(org)
          setLoadError(null)
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id, getOrganizationById])

  async function savePayload(payload) {
    setSubmitting(true)
    setServerErrors({})
    try {
      await updateOrganization(id, payload)
      pushToast({ message: 'Organization saved', variant: 'success' })
      navigate(`/organizations/${id}`, { replace: true })
    } catch (e) {
      const fieldErrors = (e as ApiClientError).fieldErrors
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        setServerErrors(fieldErrors)
      }
    } finally {
      setSubmitting(false)
      setStatusConfirmOpen(false)
      setPendingPayload(null)
    }
  }

  async function handleSubmit(payload) {
    clearError()
    const previousStatus = initial?.status
    const nextStatus = payload?.status
    if (statusChangeNeedsConfirmation(previousStatus, nextStatus)) {
      setPendingPayload(payload)
      setStatusConfirmOpen(true)
      return
    }
    await savePayload(payload)
  }

  if (loadError) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50/50 p-8 text-center">
        <p className="text-red-800">{loadError}</p>
        <Link to="/organizations" className="mt-4 inline-block">
          <Button type="button" variant="secondary">
            Back to list
          </Button>
        </Link>
      </div>
    )
  }

  if (!initial) {
    return <SkeletonFormSections />
  }

  const formValues = {
    name: initial.name,
    country: initial.country,
    currency: initial.currency,
    status: initial.status,
    businessDescription: initial.businessDescription ?? '',
    contactStreet: initial.contactStreet ?? initial.contactCity ?? '',
    contactPostcode: initial.contactPostcode ?? '',
    contactRegion: initial.contactRegion ?? '',
    contactCountry: initial.contactCountry ?? '',
    owner: {
      firstName: initial.owner?.firstName ?? initial.ownerAccess?.firstName ?? '',
      lastName: initial.owner?.lastName ?? initial.ownerAccess?.lastName ?? '',
      email: initial.owner?.email ?? initial.ownerAccess?.email ?? initial.email ?? '',
    },
    phone: initial.phone ?? '',
    storefront: normalizeStorefront(initial.storefront),
    features: { ...initial.features },
  }

  return (
    <div className="space-y-4">
      <Modal
        open={statusConfirmOpen}
        onClose={() => {
          if (!submitting) {
            setStatusConfirmOpen(false)
            setPendingPayload(null)
          }
        }}
        title="Confirm status change"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              disabled={submitting}
              onClick={() => {
                setStatusConfirmOpen(false)
                setPendingPayload(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={submitting}
              onClick={() => pendingPayload && void savePayload(pendingPayload)}
            >
              {submitting ? 'Saving…' : 'Confirm change'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground">
          {statusChangeConfirmMessage(pendingPayload?.status)}
        </p>
      </Modal>

      {error ? (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      <OrganizationForm
        mode="edit"
        initialValues={formValues}
        onSubmit={handleSubmit}
        submitting={submitting}
        onCancel={() => navigate(`/organizations/${id}`)}
        serverErrors={serverErrors}
      />
    </div>
  )
}
